
import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const BrackenfellPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Brackenfell page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Brackenfell Painters",
      "description": "Professional painting contractors in Brackenfell, Western Cape. Get quotes for interior, exterior, roof and commercial painting services.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/brackenfell-painters`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Brackenfell",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8667",
        "longitude": "18.7167"
      },
      "serviceArea": {
        "@type": "City",
        "name": "Brackenfell"
      },
      "services": [
        "Residential Interior Painting",
        "Exterior House Painting",
        "Roof Painting",
        "Commercial Painting",
        "Premium Home Painting",
        "Surface Preparation"
      ]
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Brackenfell Painters - Premium Painting Contractors Western Cape | Free Quotes";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium painting contractors in Brackenfell, Western Cape. Get free quotes for interior, exterior, roof and commercial painting. Quality homes, expert service, guaranteed results.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/b095eed72df0b1bc9128f063ce79585f.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="block">Brackenfell</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Get quotes from the best painting contractors in Brackenfell
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Premium Painting Services in Brackenfell
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Discover Brackenfell's finest painting contractors through our exclusive network. We connect discerning homeowners and businesses with skilled professionals who understand the premium quality standards and sophisticated aesthetic preferences that define this prestigious Northern Suburbs community.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our Brackenfell painting specialists excel in delivering exceptional results for the area's quality homes and commercial properties. Each contractor is carefully selected for their expertise in premium finishes, attention to detail, and commitment to the high standards that Brackenfell residents expect and deserve.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're enhancing a family estate, updating a luxury home, or maintaining commercial property in Brackenfell's thriving business districts, our contractors bring the expertise and professionalism required to exceed your expectations while preserving the area's reputation for excellence.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Premium Quality Homes</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Luxury Finishes</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Expert Craftsmanship</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Prestigious Community</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20premium%20homes%20in%20Brackenfell%20with%20luxury%20residential%20setting%20and%20high-end%20architectural%20features%2C%20quality%20painting%20equipment%2C%20clean%20professional%20appearance%2C%20bright%20natural%20lighting&width=600&height=400&seq=brackenfell-painting-work&orientation=landscape"
                alt="Professional painters working in Brackenfell"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Brackenfell Premium Market */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            Brackenfell's Premium Property Market and Painting Excellence
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-6">
                Brackenfell stands as one of the Northern Suburbs' most desirable residential areas, known for its quality homes, excellent schools, and strong property values. This reputation creates specific expectations for painting contractors who work in the area, requiring not just technical skill but an understanding of luxury finishes and premium materials.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The architectural diversity in Brackenfell ranges from elegant family homes to contemporary luxury properties, each requiring specialized painting techniques and high-end materials. Our network of Brackenfell painting professionals understands these requirements and consistently delivers results that enhance property values and maintain the area's prestigious reputation.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Brackenfell's discerning residents expect painting contractors who can work with premium materials, understand color theory and design principles, and deliver flawless finishes that complement the area's sophisticated aesthetic. Our contractors bring this expertise to every project, ensuring results that exceed expectations.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Brackenfell%20South%20Africa%20premium%20residential%20neighborhood%20with%20luxury%20homes%2C%20quality%20architecture%2C%20well-maintained%20properties%2C%20upscale%20suburban%20setting%2C%20professional%20painting%20work&width=600&height=400&seq=brackenfell-premium&orientation=landscape"
                alt="Brackenfell premium neighborhood"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Brackenfell Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Exclusive Brackenfell Painting Services
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Premium painting solutions designed for Brackenfell's quality homes and sophisticated property owners
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Luxury Home Painting</h5>
              <p className="text-gray-600">Specialized painting services for Brackenfell's premium homes, using the finest materials and techniques to create stunning finishes that enhance luxury living spaces and architectural features.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Exterior Excellence</h5>
              <p className="text-gray-600">Premium exterior painting that maintains Brackenfell's high property standards, using weather-resistant luxury finishes that protect and beautify quality homes for years to come.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Interior Design Painting</h5>
              <p className="text-gray-600">Sophisticated interior painting services that transform Brackenfell homes into stunning showcases, with expert color consultation and premium finishes that reflect refined taste.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Commercial Painting</h5>
              <p className="text-gray-600">Professional painting for Brackenfell's business district, supporting the area's commercial success with premium finishes that create impressive, professional environments.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-brush-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Specialty Finishes</h5>
              <p className="text-gray-600">Exclusive decorative painting and specialty finishes that create unique visual effects, perfect for Brackenfell's discerning homeowners who appreciate exceptional craftsmanship.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Premium Preparation</h5>
              <p className="text-gray-600">Meticulous surface preparation using premium materials and techniques, ensuring perfect paint adhesion and longevity that meets Brackenfell's exacting quality standards.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Brackenfell Painters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Brackenfell Painting Network
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connecting you with Brackenfell's most skilled and quality-focused painting professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Brackenfell Premium Expertise</h5>
              <p className="text-gray-600">Our contractors understand Brackenfell's premium property market, luxury finish requirements, and the sophisticated expectations of this prestigious community's residents.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Brackenfell Quality Excellence</h5>
              <p className="text-gray-600">All our Brackenfell painters are committed to delivering exceptional workmanship that enhances property values and maintains the area's reputation for quality and excellence.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Brackenfell Professional Service</h5>
              <p className="text-gray-600">Reliable, professional contractors who understand the importance of discretion, quality, and timely completion in Brackenfell's discerning residential community.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Brackenfell Premium Value</h5>
              <p className="text-gray-600">Competitive pricing for premium services that reflect the quality expectations and property values of Brackenfell's sophisticated real estate market.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Brackenfell Area Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Complete Brackenfell Area Coverage
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Residential Estates</h6>
              <p className="text-gray-600">Premium painting services for Brackenfell's luxury residential estates and quality family neighborhoods, ensuring exceptional results for discerning homeowners.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Brackenfell Commercial District</h6>
              <p className="text-gray-600">Professional painting for Brackenfell's thriving business areas, supporting commercial success with premium finishes that create impressive professional environments.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Greater Brackenfell Area</h6>
              <p className="text-gray-600">Comprehensive coverage throughout the broader Brackenfell region, including surrounding premium developments and commercial zones that demand exceptional quality.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Brackenfell Painting Quotes
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to enhance your Brackenfell property? Connect with premium painting contractors who understand your quality expectations and deliver exceptional results
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Brackenfell Residents Say
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Brackenfell homeowners and business owners who found their perfect premium painting contractor through our exclusive network
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default BrackenfellPage;
